import React from 'react';
import { useTranslation } from 'react-i18next';
import { fmt, numberLocale } from '../data/metrics';
import type { EventRow, EventsOverview } from '../types';
import { Delta } from './shared/Delta';

/** Percent change of `value` against `compare`, one decimal; null without a base. */
function pctChange(value: number, compare: number | undefined): number | null {
  if (compare == null || !compare) return null;
  return Math.round(((value - compare) / compare) * 1000) / 10;
}

function ptChange(value: number | null, compare: number | null | undefined): number | null {
  if (value == null || compare == null) return null;
  return Math.round((value - compare) * 10) / 10;
}

function dateLabel(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString(numberLocale(), { day: 'numeric', month: 'short' });
}

function pct(v: number | null): string {
  return v == null ? '–' : `${v.toLocaleString(numberLocale(), { maximumFractionDigits: 1 })}%`;
}

/**
 * Events on the Performance view: every event (a shift flagged as one) that
 * took reservations in the period, busiest first, with how full its seats
 * were over the days it ran and its no-show and cancellation rates. A row
 * opens the Guests drill filtered to that event, so every tile reads it.
 */
export function EventsSection({
  data,
  loading,
  onOpenEvent,
}: {
  data?: EventsOverview;
  loading?: boolean;
  onOpenEvent?: (name: string) => void;
}) {
  const { t } = useTranslation('performance');
  // Nothing to show until the payload is here, and nothing at all for venues
  // without events: the section only exists where events do.
  if (loading && !data) return null;
  if (!data || (!data.configured && !data.events.length)) return null;
  const comparable = !!data.comparable && !!data.comparePeriod?.label;
  const showRevenue = data.events.some(e => e.sellRevenue > 0);
  const compareLabel = data.comparePeriod?.label;

  const renderDelta = (value: number | null, suffix: '%' | 'pt', invert = false, title?: string) =>
    comparable && value != null ? (
      <span className="ev-delta" title={title}>
        <Delta value={value} suffix={suffix} invert={invert} size={11} />
      </span>
    ) : null;

  return (
    <div data-ftue="perf-events" className="perf-events">
      <div className="sec-head" style={{ marginTop: 22 }}>
        <h2>{t('events.title')}</h2>
        <span className="sh-meta">
          {data.events.length
            ? t('events.meta', {
                count: data.totals.events,
                oneOff: data.totals.oneOff,
                recurring: data.totals.recurring,
                period: data.periodLabel,
              })
            : t('events.metaQuiet', { count: data.configured, period: data.periodLabel })}
          {data.events.length && onOpenEvent ? ` ${t('events.hintClickRow')}` : ''}
        </span>
      </div>
      {data.events.length ? (
        <div className="opt-table-wrap">
          <table className="opt-metric-table sm-table events-table">
            <thead>
              <tr>
                <th className="opt-metric-out">{t('events.colEvent')}</th>
                <th className="opt-metric-col">{t('events.colWhen')}</th>
                <th className="opt-metric-col">{t('events.colReservations')}</th>
                <th className="opt-metric-col">{t('events.colGuests')}</th>
                <th className="opt-metric-col" title={t('events.fillTip')}>
                  {t('events.colFill')}
                </th>
                <th className="opt-metric-col">{t('events.colNoshow')}</th>
                <th className="opt-metric-col">{t('events.colCancel')}</th>
                {showRevenue ? <th className="opt-metric-col">{t('events.colPreorders')}</th> : null}
              </tr>
            </thead>
            <tbody>
              {data.events.map((e: EventRow) => {
                const when =
                  e.firstDate && e.lastDate && e.firstDate !== e.lastDate
                    ? `${dateLabel(e.firstDate)} – ${dateLabel(e.lastDate)}`
                    : dateLabel(e.firstDate);
                const open = onOpenEvent ? () => onOpenEvent(e.name) : undefined;
                return (
                  <tr
                    key={e.key}
                    className={open ? 'is-clickable' : undefined}
                    tabIndex={open ? 0 : undefined}
                    onClick={open}
                    onKeyDown={
                      open
                        ? ev => {
                            if (ev.key === 'Enter' || ev.key === ' ') {
                              ev.preventDefault();
                              open();
                            }
                          }
                        : undefined
                    }
                    title={open ? t('events.openTitle', { name: e.name }) : undefined}
                  >
                    <td className="opt-metric-out">
                      <span className="ev-name">{e.name}</span>
                      <span className={`ev-kind is-${e.kind}`}>{t(`events.kind.${e.kind}`)}</span>
                      {e.outletCount > 1 ? (
                        <span className="ev-sub">{t('events.outlets', { count: e.outletCount })}</span>
                      ) : null}
                    </td>
                    <td>
                      {when}
                      <span className="ev-sub">{t('events.occurrences', { count: e.occurrences })}</span>
                    </td>
                    <td>
                      {fmt.num(e.reservations)}
                      {renderDelta(pctChange(e.reservations, e.compare?.reservations), '%', false, compareLabel && e.compare ? `${compareLabel}: ${fmt.num(e.compare.reservations)}` : undefined)}
                    </td>
                    <td>
                      {fmt.num(e.guests)}
                      {renderDelta(pctChange(e.guests, e.compare?.guests), '%', false, compareLabel && e.compare ? `${compareLabel}: ${fmt.num(e.compare.guests)}` : undefined)}
                    </td>
                    <td>
                      {pct(e.fillPct)}
                      {renderDelta(ptChange(e.fillPct, e.compare?.fillPct), 'pt')}
                    </td>
                    <td>
                      {pct(e.noshowPct)}
                      {renderDelta(ptChange(e.noshowPct, e.compare?.noshowPct), 'pt', true)}
                    </td>
                    <td>
                      {pct(e.cancelPct)}
                      {renderDelta(ptChange(e.cancelPct, e.compare?.cancelPct), 'pt', true)}
                    </td>
                    {showRevenue ? (
                      <td>
                        {fmt.num(Math.round(e.sellRevenue))}
                        {renderDelta(pctChange(e.sellRevenue, e.compare?.sellRevenue), '%')}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data.quiet ? (
            <p className="ev-foot">{t('events.quiet', { count: data.quiet })}</p>
          ) : null}
        </div>
      ) : (
        <p className="ev-foot">{t('events.none', { count: data.configured })}</p>
      )}
    </div>
  );
}
